import { Fragment, useState } from 'react';
import { MagnifyingGlassIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useVanOrders, useVanOrder, useVanOrderStorerooms } from '../hooks/useVanOrders';

// Every van stock order, and what was actually picked against it.
//
// Until this existed the two numbers only met in the database: FSM shows an
// engineer his own orders and the storeroom its open queue, and a collected
// order left both. That is how 91 lines went out over-picked by 511 units
// before an engineer queried his own van.
//
// The requested-vs-picked gap is therefore the point of the screen, not a
// detail on it.

const STATUS_CLS = {
  submitted: 'bg-amber-100 text-amber-800',
  picked: 'bg-blue-100 text-blue-800',
  collected: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-gray-100 text-gray-600',
};

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');
const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 });

// Only an auto-suggested line can be over-picked: a manual add starts at zero
// requested by definition, so flagging it would cry wolf on every order.
function OrderLines({ id }) {
  const { data, isLoading } = useVanOrder(id);
  if (isLoading || !data) return <p className="px-4 py-3 text-xs text-gray-400">Loading lines…</p>;
  const lines = data.lines || [];
  if (!lines.length) return <p className="px-4 py-3 text-xs text-gray-400">No lines on this order.</p>;
  return (
    <table className="min-w-full text-sm">
      <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
        <tr>
          <th className="px-4 py-1.5">Item</th>
          <th className="px-4 py-1.5">Source</th>
          <th className="px-4 py-1.5 text-right">On van</th>
          <th className="px-4 py-1.5 text-right">Requested</th>
          <th className="px-4 py-1.5 text-right">Picked</th>
          <th className="px-4 py-1.5 text-right">Over</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {lines.map((l) => {
          const auto = l.source === 'isp_auto';
          const over = auto ? Math.max(Number(l.qty_picked || 0) - Number(l.qty_requested || 0), 0) : 0;
          return (
            <tr key={l.id} className={over > 0 ? 'bg-rose-50/60' : undefined}>
              <td className="px-4 py-1.5 text-gray-800">{l.name || l.item_code}</td>
              <td className="px-4 py-1.5 text-xs text-gray-500">
                {l.source === 'isp_auto' ? 'suggested' : l.source === 'isp_manual' ? 'added by picker' : 'consumable'}
              </td>
              <td className="px-4 py-1.5 text-right tabular-nums text-gray-500">
                {l.van_on_hand_at_request == null ? '—' : num(l.van_on_hand_at_request)}
              </td>
              <td className="px-4 py-1.5 text-right tabular-nums">{auto ? num(l.qty_requested) : '—'}</td>
              <td className="px-4 py-1.5 text-right tabular-nums">{l.qty_picked == null ? '—' : num(l.qty_picked)}</td>
              <td className="px-4 py-1.5 text-right tabular-nums">
                {over > 0 ? <span className="font-semibold text-rose-700">+{num(over)}</span> : ''}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function VanOrders() {
  const [filters, setFilters] = useState({ status: '', storeroom_id: '', from: '', to: '', q: '' });
  const [open, setOpen] = useState(null);
  const { data, isLoading, isFetching, refetch } = useVanOrders(filters);
  const { data: storerooms } = useVanOrderStorerooms();

  const set = (k) => (e) => { setFilters((f) => ({ ...f, [k]: e.target.value })); setOpen(null); };
  const rows = data?.requests || [];
  const totals = data?.totals || { orders: 0, over_lines: 0, over_units: 0 };
  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Van Stock Orders</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Every order engineers have raised, and what the storeroom actually picked against it.
            An auto-suggested line picked above the request is flagged.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
          <div className="text-xl font-bold tabular-nums text-gray-900">{num(totals.orders)}</div>
          <div className="text-xs text-gray-500">orders shown</div>
        </div>
        <div className={`rounded-lg border px-4 py-2 ${totals.over_units > 0 ? 'border-rose-200 bg-rose-50' : 'border-gray-200 bg-white'}`}>
          <div className={`text-xl font-bold tabular-nums ${totals.over_units > 0 ? 'text-rose-700' : 'text-gray-900'}`}>
            {num(totals.over_units)}
          </div>
          <div className="text-xs text-gray-500">units picked above request</div>
        </div>
        <div className={`rounded-lg border px-4 py-2 ${totals.over_lines > 0 ? 'border-rose-200 bg-rose-50' : 'border-gray-200 bg-white'}`}>
          <div className={`text-xl font-bold tabular-nums ${totals.over_lines > 0 ? 'text-rose-700' : 'text-gray-900'}`}>
            {num(totals.over_lines)}
          </div>
          <div className="text-xs text-gray-500">lines affected</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input value={filters.q} onChange={set('q')} placeholder="Reference, engineer, van, picker…"
            className={`${sel} w-64 pl-8`} />
        </div>
        <select value={filters.status} onChange={set('status')} className={sel}>
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="picked">Picked</option>
          <option value="collected">Collected</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filters.storeroom_id} onChange={set('storeroom_id')} className={sel}>
          <option value="">All storerooms</option>
          {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <label className="text-xs text-gray-500">From
          <input type="date" value={filters.from} onChange={set('from')} className={`${sel} ml-1`} />
        </label>
        <label className="text-xs text-gray-500">To
          <input type="date" value={filters.to} onChange={set('to')} className={`${sel} ml-1`} />
        </label>
      </div>

      <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2.5">Reference</th>
              <th className="px-4 py-2.5">Engineer</th>
              <th className="px-4 py-2.5">Van</th>
              <th className="px-4 py-2.5">Storeroom</th>
              <th className="px-4 py-2.5">Picked by</th>
              <th className="px-4 py-2.5">Submitted</th>
              <th className="px-4 py-2.5 text-right">Lines</th>
              <th className="px-4 py-2.5 text-right">Over-picked</th>
              <th className="px-4 py-2.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
            ) : rows.map((r) => {
              const over = Number(r.over_picked_units || 0);
              return (
                <Fragment key={r.id}>
                  <tr onClick={() => setOpen(open === r.id ? null : r.id)}
                    className={`cursor-pointer ${over > 0 ? 'bg-rose-50/40 hover:bg-rose-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-brand">{r.reference}</td>
                    <td className="px-4 py-2.5 text-gray-800">{r.engineer_name}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-600">{r.van_registration || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.storeroom_name || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{r.picked_by_name || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">{fmtDate(r.submitted_at)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{r.line_count}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {over > 0 ? (
                        <span className="inline-flex items-center gap-1 font-semibold text-rose-700">
                          <ExclamationTriangleIcon className="h-4 w-4" />+{num(over)}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CLS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                  {open === r.id ? (
                    <tr><td colSpan={9} className="bg-gray-50 p-0"><OrderLines id={r.id} /></td></tr>
                  ) : null}
                </Fragment>
              );
            })}
            {!isLoading && !rows.length ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No orders match those filters.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VanOrders;
